import type { Player } from '@schibsted-svp/web-player';
import type { ResolvedPermissionValue } from '@vgtv/consent-manager';

import { getAdConsentKeywords } from './getAdPermission';
import { getTcfConsentKeywords } from './getTcfConsentKeywords';
import { prepareGlimrKeywords } from './glimr';
import type { GlimrConfig, XandrKeywords } from './types';

type BuildConsentKeywordsParams = {
  player: Player | undefined;
  glimrConfig: GlimrConfig | undefined;
  cmpConsent: ResolvedPermissionValue;
  // Whether to append the tcf/gdpr consent keywords. Defaults to `true`.
  includeTcfConsent?: boolean;
  // Whether to append the cmp advertising keywords. Defaults to `true`.
  includeAdConsent?: boolean;
};

// Merges the consent related keyword sources (cmp advertising, glimr and
// tcf/gdpr). The sources have disjoint keys, so merge order does not affect
// the result. This disjointness relies on glimr's configured `keywordPrefix`
// never being `cmp_advertising` or `gdpr`; keep that invariant if the glimr
// config changes. Returns keywords already in their final form; the stage
// applies no prefixing of its own (sources define `kw_` at source where needed).
export async function buildConsentKeywords({
  player,
  glimrConfig,
  cmpConsent,
  includeTcfConsent = true,
  includeAdConsent = true,
}: BuildConsentKeywordsParams): Promise<Partial<XandrKeywords>> {
  const [glimrKeywords, tcfKeywords] = await Promise.all([
    prepareGlimrKeywords({ player, config: glimrConfig }),
    includeTcfConsent ? getTcfConsentKeywords() : {},
  ]);

  return Object.assign(
    {},
    includeAdConsent ? getAdConsentKeywords(cmpConsent) : {},
    glimrKeywords,
    tcfKeywords
  );
}
